import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Search, UserCog, Shield, Power, History, RotateCcw, Save, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  { value: "admin", label: "Administrador" },
  { value: "technician", label: "Técnico" },
  { value: "reception", label: "Recepción" },
  { value: "billing", label: "Facturación" },
  { value: "read_only", label: "Solo lectura" },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));

const ACTION_LABEL = {
  "user.created": "Usuario creado",
  "user.updated": "Usuario editado",
  "user.email_changed": "Email cambiado",
  "user.role_changed": "Rol cambiado",
  "user.activated": "Usuario activado",
  "user.deactivated": "Usuario desactivado",
  "user.deleted": "Usuario ELIMINADO",
  "user.password_changed": "Contraseña cambiada",
  "user.password_reset_by_admin": "Contraseña restablecida por admin",
  "auth.login_success": "Inicio de sesión",
  "auth.login_failed": "Intento fallido",
  "auth.locked": "Cuenta bloqueada",
};

function genTempPwd() {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const dig = "23456789";
  const sym = "!#$%&*?";
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let p = pick(upper) + pick(lower) + pick(dig) + pick(sym);
  for (let i = 0; i < 6; i++) p += pick(upper + lower + dig);
  return p.split("").sort(() => Math.random() - 0.5).join("");
}

export default function Users() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("users"); // users | audit
  const [users, setUsers] = useState([]);
  const [activeFilter, setActiveFilter] = useState("true");
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/users", { params: { active: activeFilter } });
      setUsers(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [activeFilter]);
  const loadAudit = useCallback(async () => {
    try {
      const r = await api.get("/audit-log", { params: { limit: 300 } });
      setAudit(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === "audit") loadAudit();
  }, [tab, loadAudit]);

  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    return (u.name || "").toLowerCase().includes(n) ||
      (u.email || "").toLowerCase().includes(n) ||
      (u.roles || []).some((r) => ROLE_LABEL[r]?.toLowerCase().includes(n));
  });

  const toggleActive = async (u) => {
    if (u.id === me?.id) {
      toast.error("No puedes desactivar tu propia cuenta");
      return;
    }
    try {
      if (u.active) {
        await api.delete(`/users/${u.id}`);
        toast.success(`${u.email} desactivado`);
      } else {
        await api.put(`/users/${u.id}`, { active: true });
        toast.success(`${u.email} reactivado`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const purgeUser = async (u) => {
    try {
      await api.delete(`/users/${u.id}/purge`);
      toast.success(`${u.email} eliminado definitivamente`);
      setPurgeTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 lg:px-8 pt-6 lg:pt-8 pb-4 border-b border-dashed border-[#D5D7CE]" style={{ background: "var(--bg)" }}>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
              <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Control de acceso
            </div>
            <h1 className="font-display text-3xl md:text-[38px] font-black mt-2 tracking-tight">Usuarios</h1>
            <p className="text-sm text-[#4B5158] mt-1">
              Sistema cerrado. Solo el administrador puede crear y gestionar cuentas.
            </p>
          </div>
          {tab === "users" && (
            <button
              onClick={() => setShowCreate(true)}
              data-testid="users-new"
              className="ds-btn primary"
            >
              <Plus size={14} /> Crear usuario
            </button>
          )}
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <div className="ds-pager">
            {[
              { v: "users", l: "Usuarios", I: UserCog },
              { v: "audit", l: "Auditoría", I: History },
            ].map(({ v, l, I }) => (
              <button
                key={v}
                onClick={() => setTab(v)}
                data-testid={`users-tab-${v}`}
                className={tab === v ? "!bg-[#0F1210] !text-white" : ""}
              >
                <I size={12} /> {l}
              </button>
            ))}
          </div>
          {tab === "users" && (
            <>
              <div className="relative flex-1 max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9098]" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por nombre, email o rol..."
                  data-testid="users-search"
                  className="ds-input pl-9"
                />
              </div>
              <div className="ds-pager">
                {[["true", "Activos"], ["false", "Desactivados"], ["all", "Todos"]].map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => setActiveFilter(v)}
                    data-testid={`users-filter-${v}`}
                    className={activeFilter === v ? "!bg-[#0F1210] !text-white" : ""}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </header>

      <div className="flex-1 p-6 lg:p-10">
        {tab === "users" ? (
          <div className="ds-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b-2 border-[#111110]">
                  <th className="px-4 py-2.5">Usuario</th>
                  <th className="px-4 py-2.5">Email</th>
                  <th className="px-4 py-2.5">Roles</th>
                  <th className="px-4 py-2.5">Estado</th>
                  <th className="px-4 py-2.5">Último acceso</th>
                  <th className="px-4 py-2.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[#525252]">Sin usuarios.</td></tr>
                ) : filtered.map((u) => (
                  <tr key={u.id} className="border-b border-[#E5E5E0] hover:bg-[#F7F7F4]" data-testid={`users-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{u.name}</div>
                      {u.must_change_password && (
                        <div className="font-mono text-[9px] text-amber-700 uppercase">debe cambiar contraseña</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(u.roles || []).map((r) => (
                          <span key={r} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#F0F4E8] text-[#556C2E] border border-[#6E8B3D] font-mono text-[10px] uppercase">
                            <Shield size={9} /> {ROLE_LABEL[r] || r}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {u.active ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-300 font-mono text-[10px] uppercase">activo</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-neutral-100 text-neutral-600 border border-neutral-400 font-mono text-[10px] uppercase">inactivo</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-[10px] text-[#525252]">
                      {u.last_login ? new Date(u.last_login).toLocaleString("es") : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => setEditing(u)}
                          data-testid={`users-edit-${u.id}`}
                          className="border border-[#111110] hover:bg-[#111110] hover:text-white px-2 py-1 text-[11px] font-mono uppercase"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => toggleActive(u)}
                          disabled={u.id === me?.id}
                          data-testid={`users-toggle-${u.id}`}
                          className={`border px-2 py-1 text-[11px] font-mono uppercase inline-flex items-center gap-1 disabled:opacity-30 ${
                            u.active ? "border-red-300 text-red-700 hover:bg-red-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          }`}
                        >
                          <Power size={11} /> {u.active ? "Desactivar" : "Activar"}
                        </button>
                        {!u.active && u.id !== me?.id && (
                          <button
                            onClick={() => setPurgeTarget(u)}
                            data-testid={`users-purge-${u.id}`}
                            title="Eliminar definitivamente"
                            className="border border-red-700 text-white bg-red-700 hover:bg-red-800 px-2 py-1 text-[11px] font-mono uppercase inline-flex items-center gap-1"
                          >
                            <Trash2 size={11} /> Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <AuditTable audit={audit} />
        )}
      </div>

      {showCreate && <UserDialog onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {editing && <UserDialog user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {purgeTarget && (
        <PurgeDialog
          user={purgeTarget}
          onClose={() => setPurgeTarget(null)}
          onConfirm={() => purgeUser(purgeTarget)}
        />
      )}
    </div>
  );
}

function PurgeDialog({ user, onClose, onConfirm }) {
  const [text, setText] = useState("");
  const canDelete = text.trim() === "ELIMINAR";
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-red-700 shadow-hard w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-red-700 bg-red-700 text-white">
          <div className="font-display font-bold text-sm flex items-center gap-2">
            <Trash2 size={14} /> Eliminar usuario definitivamente
          </div>
          <button onClick={onClose} className="p-1 hover:bg-red-800"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-sm">
            Vas a eliminar <span className="font-mono font-bold">{user.email}</span> ({user.name}) <strong>de forma definitiva</strong>.
          </div>
          <div className="text-xs bg-amber-50 border border-amber-300 text-amber-900 px-3 py-2 space-y-1">
            <div className="font-semibold">Esta acción es irreversible.</div>
            <div>· El usuario no aparecerá más en el sistema.</div>
            <div>· El historial de reparaciones, facturas y comentarios donde aparecía su nombre se conservará como texto (no se borra).</div>
            <div>· La acción quedará registrada en la auditoría.</div>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase text-[#525252]">
              Para confirmar, escribe <span className="text-red-700 font-bold">ELIMINAR</span>
            </label>
            <input
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="purge-confirm-input"
              className="mt-1 w-full ds-card focus:border-red-700 outline-none px-3 py-2 text-sm font-mono rounded-none"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 border border-[#111110] px-3 py-2 text-sm">
              Cancelar
            </button>
            <button
              onClick={onConfirm}
              disabled={!canDelete}
              data-testid="purge-confirm-btn"
              className="flex-1 bg-red-700 hover:bg-red-800 text-white px-3 py-2 text-sm font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-1"
            >
              <Trash2 size={13} /> Eliminar para siempre
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserDialog({ user, onClose, onSaved }) {
  const editing = !!user;
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [specialty, setSpecialty] = useState(user?.specialty || "");
  const [roles, setRoles] = useState(user?.roles || ["technician"]);
  const [tempPwd, setTempPwd] = useState(editing ? "" : genTempPwd());
  const [forceChange, setForceChange] = useState(true);
  const [saving, setSaving] = useState(false);

  const toggleRole = (r) => {
    setRoles(roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!roles.length) {
      toast.error("Asigna al menos un rol");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = { name, email, phone, specialty, roles };
        if (tempPwd) {
          payload.password = tempPwd;
          payload.force_password_change = forceChange;
        }
        await api.put(`/users/${user.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", {
          name, email, phone, specialty, roles,
          temp_password: tempPwd,
          force_password_change: forceChange,
        });
        toast.success(`Usuario ${email} creado. Contraseña temporal: ${tempPwd}`);
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#111110] shadow-hard w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
          <div className="font-display font-bold text-sm">{editing ? "Editar usuario" : "Crear usuario"}</div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]"><X size={14} /></button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase text-[#525252]">Nombre *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="ud-name"
                className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase text-[#525252]">Email *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="ud-email"
                className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase text-[#525252]">Teléfono</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="ud-phone"
                className="mt-1 w-full ds-card outline-none px-3 py-2 text-sm rounded-none" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase text-[#525252]">Especialidad</label>
              <input value={specialty} onChange={(e) => setSpecialty(e.target.value)} data-testid="ud-specialty"
                className="mt-1 w-full ds-card outline-none px-3 py-2 text-sm rounded-none" />
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase text-[#525252]">Roles (un usuario puede tener varios)</label>
            <div className="mt-1 grid grid-cols-2 gap-1.5">
              {ROLES.map((r) => (
                <label key={r.value} className={`flex items-center gap-2 border px-2 py-1.5 cursor-pointer ${
                  roles.includes(r.value) ? "border-[#6E8B3D] bg-[#F0F4E8]" : "border-[#E5E5E0]"
                }`}>
                  <input
                    type="checkbox"
                    checked={roles.includes(r.value)}
                    onChange={() => toggleRole(r.value)}
                    data-testid={`ud-role-${r.value}`}
                    className="accent-[#6E8B3D]"
                  />
                  <span className="text-xs">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase text-[#525252]">
              {editing ? "Restablecer contraseña (opcional)" : "Contraseña temporal *"}
            </label>
            <div className="mt-1 flex gap-2">
              <input
                value={tempPwd}
                onChange={(e) => setTempPwd(e.target.value)}
                placeholder={editing ? "Vacío = no cambiar" : ""}
                data-testid="ud-temp-pwd"
                className="flex-1 ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm font-mono rounded-none"
              />
              <button
                type="button"
                onClick={() => setTempPwd(genTempPwd())}
                className="border border-[#111110] hover:bg-[#111110] hover:text-white px-2 py-2 text-xs font-mono uppercase inline-flex items-center gap-1"
                title="Generar contraseña fuerte"
                data-testid="ud-gen-pwd"
              >
                <RotateCcw size={12} /> Generar
              </button>
            </div>
            <p className="text-[10px] text-[#525252] mt-1">
              Min 8 caracteres, 1 mayúscula, 1 minúscula, 1 número. Recomendado símbolo.
            </p>
            {tempPwd && (
              <label className="flex items-center gap-2 mt-2 text-xs">
                <input type="checkbox" checked={forceChange} onChange={(e) => setForceChange(e.target.checked)} className="accent-[#6E8B3D]" data-testid="ud-force-change" />
                Forzar cambio en el primer inicio de sesión
              </label>
            )}
          </div>

          <button type="submit" disabled={saving} data-testid="ud-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2.5 text-sm font-semibold disabled:opacity-50">
            <Save size={14} /> {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear usuario"}
          </button>
        </form>
      </div>
    </div>
  );
}

function AuditTable({ audit }) {
  return (
    <div className="ds-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b-2 border-[#111110]">
            <th className="px-4 py-2.5">Fecha</th>
            <th className="px-4 py-2.5">Acción</th>
            <th className="px-4 py-2.5">Usuario afectado</th>
            <th className="px-4 py-2.5">Realizado por</th>
            <th className="px-4 py-2.5">IP</th>
            <th className="px-4 py-2.5">Detalle</th>
          </tr>
        </thead>
        <tbody>
          {audit.length === 0 ? (
            <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[#525252]">Sin eventos.</td></tr>
          ) : audit.map((e) => (
            <tr key={e.id} className="border-b border-[#E5E5E0]" data-testid={`audit-row-${e.id}`}>
              <td className="px-4 py-2 font-mono text-[10px]">{new Date(e.at).toLocaleString("es")}</td>
              <td className="px-4 py-2">
                <span className={`inline-flex items-center px-1.5 py-0.5 font-mono text-[10px] uppercase border ${
                  e.action.startsWith("auth.login_failed") || e.action === "auth.locked"
                    ? "bg-red-50 text-red-700 border-red-300"
                    : e.action.startsWith("auth.")
                    ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                    : "bg-[#F0F4E8] text-[#556C2E] border-[#6E8B3D]"
                }`}>
                  {ACTION_LABEL[e.action] || e.action}
                </span>
              </td>
              <td className="px-4 py-2 font-mono text-[11px]">{e.target_email || "—"}</td>
              <td className="px-4 py-2 font-mono text-[11px]">{e.by_user_email || "—"}</td>
              <td className="px-4 py-2 font-mono text-[10px] text-[#525252]">{e.ip || "—"}</td>
              <td className="px-4 py-2 text-[11px] text-[#525252]">
                {e.payload && Object.keys(e.payload).length ? JSON.stringify(e.payload) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
